import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import DescriptionIcon from '@mui/icons-material/Description';

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const StatCard = ({ label, value, subtext, icon, color }) => (
  <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, position: 'relative', overflow: 'hidden', height: '100%' }}>
    <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: `${color}20`, color: color }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h4" color="text.primary">{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subtext && <Typography variant="caption" sx={{ color: color }}>{subtext}</Typography>}
    </Box>
  </Paper>
);

const MigrationEstimator = () => {
  const [docType, setDocType] = useState('');
  const [count, setCount] = useState('');
  const [estimates, setEstimates] = useState([]);

  // Current calculation based on inputs
  const records = parseFloat(count) || 0;
  // Logic: 10k records = 1hr export, 1.5hr import
  const currentExportHrs = records / 10000;
  const currentImportHrs = (records / 10000) * 1.5;
  const currentTotalHrs = currentExportHrs + currentImportHrs;
  const currentTotalDays = currentTotalHrs / 24;

  const handleAddEstimate = () => {
    if (!docType || !count) return;

    const newEstimate = {
      id: Date.now(),
      type: docType,
      count: records,
      exportHrs: currentExportHrs,
      importHrs: currentImportHrs,
      totalDays: currentTotalDays
    };

    setEstimates([...estimates, newEstimate]);
    // Optional: Clear inputs after add
    // setDocType('');
    // setCount('');
  };

  const handleDelete = (id) => {
    setEstimates(estimates.filter(e => e.id !== id));
  };

  const handleExport = (format) => {
    if (estimates.length === 0) return;

    const data = estimates.map(e => ({
      'Document Type': e.type,
      'Record Count': e.count,
      'Export Duration (hrs)': e.exportHrs.toFixed(2),
      'Import Duration (hrs)': e.importHrs.toFixed(2),
      'Total Time (days)': e.totalDays.toFixed(2)
    }));

    if (format === 'csv') {
      const ws = XLSX.utils.json_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "migration_estimates.csv";
      link.click();
    } else if (format === 'xlsx') {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Estimates");
      XLSX.writeFile(wb, "migration_estimates.xlsx");
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      doc.text("Migration Estimates", 14, 20);

      const tableColumn = ["Document Type", "Records", "Export (hrs)", "Import (hrs)", "Total (days)"];
      const tableRows = estimates.map(e => [
        e.type,
        e.count.toLocaleString(),
        e.exportHrs.toFixed(2),
        e.importHrs.toFixed(2),
        e.totalDays.toFixed(2)
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
      });
      doc.save("migration_estimates.pdf");
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>Migration Estimator</Typography>

      {/* Input Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Tooltip title="Type of document (e.g., Invoice, Claim, Policy)" arrow placement='top'>
                  <TextField
                    fullWidth
                    label="Document Type"
                    variant="outlined"
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    placeholder="e.g., Invoices, Claims"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Tooltip title="Total number of database rows/records" arrow placement='top'>
                  <TextField
                    fullWidth
                    label="Total Record Count"
                    variant="outlined"
                    type="number"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Tooltip title="Add this estimate to the session list below" arrow placement="right">
                  <span> {/* span needed for disabled button tooltip */}
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddEstimate}
                      disabled={!docType || !count}
                      sx={{ mt: 1 }}
                    >
                      Add to Estimate List
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Enter Document type and the total number of records to be migrated to estimate downtime based on standard throughput.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Live Estimates Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Tooltip title="Estimated time to export data based on ~10k records/hr" arrow>
            <div>
              <StatCard
                label="Est. Export Duration"
                value={currentExportHrs.toFixed(2) + " h"}
                subtext="~10k records/hr"
                icon={<ImportExportIcon />}
                color="#00f2ff"
              />
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <Tooltip title="Estimated time to import data based on ~6.6k records/hr" arrow>
            <div>
              <StatCard
                label="Est. Import Duration"
                value={currentImportHrs.toFixed(2) + " h"}
                subtext="~6.6k records/hr"
                icon={<AccessTimeIcon />}
                color="#ff00a0"
              />
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <Tooltip title="Total continuous project runtime estimate" arrow>
            <div>
              <StatCard
                label="Total Project Time"
                value={currentTotalDays.toFixed(2) + " days"}
                subtext="Continuous runtime"
                icon={<CalendarTodayIcon />}
                color="#00e676"
              />
            </div>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Estimates Table */}
      {estimates.length > 0 && (
        <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Session Estimates</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Export CSV">
                <IconButton onClick={() => handleExport('csv')}>
                  <DescriptionIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Excel">
                <IconButton onClick={() => handleExport('xlsx')}>
                  <TableViewIcon color="success" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export PDF">
                <IconButton onClick={() => handleExport('pdf')}>
                  <PictureAsPdfIcon color="error" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <Tooltip title="The classification of the document set" arrow placement="top"><TableCell>Document Type</TableCell></Tooltip>
                  <Tooltip title="Total number of records" arrow placement="top"><TableCell align="right">Records</TableCell></Tooltip>
                  <Tooltip title="Time to export" arrow placement="top"><TableCell align="right">Export (hrs)</TableCell></Tooltip>
                  <Tooltip title="Time to import" arrow placement="top"><TableCell align="right">Import (hrs)</TableCell></Tooltip>
                  <Tooltip title="Total estimated days" arrow placement="top"><TableCell align="right">Total (days)</TableCell></Tooltip>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimates.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell fontWeight="medium">{row.type}</TableCell>
                    <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.exportHrs.toFixed(2)}</TableCell>
                    <TableCell align="right">{row.importHrs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.totalDays.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {estimates.length > 1 && (
                  <TableRow sx={{ bgcolor: 'action.selected' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {estimates.reduce((sum, e) => sum + e.count, 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {estimates.reduce((sum, e) => sum + e.exportHrs, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {estimates.reduce((sum, e) => sum + e.importHrs, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {estimates.reduce((sum, e) => sum + e.totalDays, 0).toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default MigrationEstimator;